import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  RefreshCw,
  History,
  FileText,
  ChevronRight,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

const QUIT_REASONS = [
  "Lost motivation",
  "Got too busy",
  "Results were slow",
  "Workouts were too hard",
  "Life got in the way",
  "Injury or health issue",
  "Needed a break",
  "Other"
];

export default function CycleReset() {
  const navigate = useNavigate();
  const { user, fetchUser } = useAuth();
  const [cycleHistory, setCycleHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [showReflection, setShowReflection] = useState(false);
  
  const [reflection, setReflection] = useState({
    selectedReasons: [],
    whatWorked: "",
    whatDidntWork: "",
    newGoal: ""
  });

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/cycle/history`);
      setCycleHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch cycle history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReasonToggle = (reason) => {
    setReflection(prev => ({
      ...prev,
      selectedReasons: prev.selectedReasons.includes(reason)
        ? prev.selectedReasons.filter(r => r !== reason)
        : [...prev.selectedReasons, reason]
    }));
  };

  const handleSaveReflection = async () => {
    try {
      await axios.post(`${API}/cycle/reflection`, {
        cycle_number: cycleHistory?.current_cycle || 1,
        why_quit_reasons: reflection.selectedReasons,
        what_worked: reflection.whatWorked,
        what_didnt_work: reflection.whatDidntWork,
        new_goal: reflection.newGoal
      });
      toast.success("Reflection saved!");
      setShowReflection(false);
    } catch (error) {
      toast.error("Failed to save reflection");
    }
  };

  const handleResetCycle = async () => {
    setResetting(true);
    try {
      await axios.post(`${API}/cycle/reset`);
      await fetchUser();
      toast.success("New cycle started! Day 1 begins now.");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to reset cycle");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Back
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">CYCLE RESET</span>
            </div>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Current Cycle Status */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Card className="bg-card border-border">
            <CardContent className="p-6 text-center">
              <RefreshCw className="h-12 w-12 text-primary mx-auto mb-4" />
              <h2 className="text-2xl font-black uppercase tracking-tighter mb-2">
                CYCLE {cycleHistory?.current_cycle || 1}
              </h2>
              <p className="text-muted-foreground mb-4">
                You're on Day {user?.current_day || 1} of your current cycle
              </p>
              
              {user?.current_day >= 60 ? (
                <div className="space-y-4">
                  <div className="p-4 bg-accent/20 rounded-sm border border-accent/50">
                    <p className="text-accent font-bold">Congratulations! You've completed the 60-Day SHRED!</p>
                  </div>
                  <Button
                    data-testid="start-new-cycle-btn"
                    onClick={handleResetCycle}
                    disabled={resetting}
                    className="bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm"
                  >
                    {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Start New Cycle"}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setShowReflection(true)}
                  className="uppercase font-bold tracking-widest rounded-sm"
                >
                  <FileText className="mr-2 h-4 w-4" /> Add Reflection
                </Button>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Cycle History */}
        {cycleHistory?.cycles?.length > 0 && (
          <Card className="bg-card border-border mb-6">
            <CardHeader>
              <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                <History className="h-5 w-5 text-primary" />
                Previous Cycles
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {cycleHistory.cycles.map((cycle, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center justify-between p-4 rounded-sm border border-border bg-secondary/30"
                  >
                    <div>
                      <div className="font-bold">Cycle {cycle.cycle_number}</div>
                      <div className="text-sm text-muted-foreground">
                        Completed on {new Date(cycle.completed_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-primary">Day {cycle.final_day}</div>
                      <div className="text-sm text-muted-foreground">{cycle.total_workouts} workouts</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reflections */}
        {cycleHistory?.reflections?.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                <FileText className="h-5 w-5 text-accent" />
                Your Reflections
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {cycleHistory.reflections.map((ref, i) => (
                  <div key={i} className="p-4 rounded-sm border border-border bg-secondary/30">
                    <div className="font-bold mb-2">Cycle {ref.cycle_number} Reflection</div>
                    {ref.what_worked && (
                      <div className="mb-2">
                        <div className="text-xs text-accent uppercase tracking-wider">What Worked</div>
                        <p className="text-sm">{ref.what_worked}</p>
                      </div>
                    )}
                    {ref.new_goal && (
                      <div>
                        <div className="text-xs text-primary uppercase tracking-wider">New Goal</div>
                        <p className="text-sm">{ref.new_goal}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Reflection Modal */}
      {showReflection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="uppercase tracking-tighter">Cycle Reflection</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <label className="text-sm uppercase tracking-wider text-muted-foreground mb-3 block">
                    If you struggled, what were the main reasons?
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {QUIT_REASONS.map((reason) => (
                      <div
                        key={reason}
                        onClick={() => handleReasonToggle(reason)}
                        className={`flex items-center gap-2 p-3 rounded-sm border cursor-pointer transition-colors ${
                          reflection.selectedReasons.includes(reason)
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <Checkbox checked={reflection.selectedReasons.includes(reason)} />
                        <span className="text-sm">{reason}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm uppercase tracking-wider text-muted-foreground mb-2 block">
                    What worked well?
                  </label>
                  <Textarea
                    value={reflection.whatWorked}
                    onChange={(e) => setReflection(prev => ({ ...prev, whatWorked: e.target.value }))}
                    placeholder="What strategies or habits helped you stay on track?"
                    className="bg-input border-transparent focus:border-primary rounded-sm"
                  />
                </div>

                <div>
                  <label className="text-sm uppercase tracking-wider text-muted-foreground mb-2 block">
                    What didn't work?
                  </label>
                  <Textarea
                    value={reflection.whatDidntWork}
                    onChange={(e) => setReflection(prev => ({ ...prev, whatDidntWork: e.target.value }))}
                    placeholder="What would you do differently next time?"
                    className="bg-input border-transparent focus:border-primary rounded-sm"
                  />
                </div>

                <div>
                  <label className="text-sm uppercase tracking-wider text-muted-foreground mb-2 block">
                    New goal for next cycle
                  </label>
                  <Textarea
                    value={reflection.newGoal}
                    onChange={(e) => setReflection(prev => ({ ...prev, newGoal: e.target.value }))}
                    placeholder="What do you want to achieve in your next cycle?"
                    className="bg-input border-transparent focus:border-primary rounded-sm"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowReflection(false)}
                    className="flex-1 uppercase font-bold tracking-widest rounded-sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveReflection}
                    className="flex-1 bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm"
                  >
                    Save Reflection
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}
    </div>
  );
}
